import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Card, Icon, Label, Button, Loader, Message } from 'semantic-ui-react';
import {
  selectPrototypes,
  selectActivePrototype,
  selectPrototypeLoading,
  fetchPrototypeDetail,
  clearActivePrototype,
} from '../../slices/litGroupPrototypeSlice';
import './PrototypeList.css';

/**
 * Status badge config
 */
const STATUS_CONFIG = {
  draft: { color: 'yellow', icon: 'pencil', text: 'Draft' },
  ready: { color: 'green', icon: 'checkmark', text: 'Ready' },
  processing: { color: 'blue', icon: 'spinner', text: 'Processing' },
  complete: { color: 'teal', icon: 'check circle', text: 'Complete' },
  archived: { color: 'grey', icon: 'archive', text: 'Archived' },
};

/**
 * PrototypeList — Left panel: clickable card list of user's prototypes
 * 
 * Props:
 *   onNewPrototype — callback to show creation form
 */
const PrototypeList = ({ onNewPrototype }) => {
  const dispatch = useDispatch();
  const prototypes = useSelector(selectPrototypes);
  const activePrototype = useSelector(selectActivePrototype);
  const loading = useSelector(selectPrototypeLoading);

  const handleSelect = (uuid) => {
    if (activePrototype?.uuid === uuid) {
      // Deselect if clicking the active one
      dispatch(clearActivePrototype());
    } else {
      dispatch(fetchPrototypeDetail(uuid));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
    } catch { return dateStr; }
  };

  return (
    <div>
      <Button
        primary
        fluid
        icon="plus"
        content="New Literary Group"
        onClick={onNewPrototype}
        style={{ marginBottom: '1em' }}
        disabled={loading.create}
      />

      {loading.list && (
        <div style={{ textAlign: 'center', padding: '2em 0' }}>
          <Loader active inline="centered" content="Loading literary groups..." />
        </div>
      )}

      {!loading.list && prototypes.length === 0 && (
        <Message info size="small">
          <Message.Header>No literary groups yet</Message.Header>
          <p>Click "New Literary Group" to create your first.</p>
        </Message>
      )}

      {/* The cards scroll; the New button above does not. */}
      <div className="prototype-list-scroll">
      {prototypes.map((proto) => {
        const isActive = activePrototype?.uuid === proto.uuid;
        const status = STATUS_CONFIG[proto.status] || STATUS_CONFIG.draft;
        const bookCount = proto.bookCount ?? 0;

        return (
          <Card
            key={proto.uuid}
            fluid
            onClick={() => handleSelect(proto.uuid)}
            style={{
              cursor: 'pointer',
              borderLeft: isActive ? '4px solid #2185d0' : '4px solid transparent',
              backgroundColor: isActive ? '#f0f7ff' : undefined,
              transition: 'all 0.15s ease',
            }}
          >
            <Card.Content>
              {/* The title gets the WHOLE line. The status badge used to sit beside it, capping
                  the name at 70% of an already narrow card — and these names differ at the END
                  ("The Last Nevergate Chronicles TOC FIXES" against "... Scene Break"), so the
                  badge was eating precisely the part that tells them apart. It has moved down to
                  the Created line, which had a whole row to itself and space to spare.
                  Still ellipsised, since some names outrun any width; the title attribute gives
                  the full one on hover. Native rather than a Popup — it costs nothing per card
                  across a long list, and it cannot swallow the click that selects the group. */}
              <Card.Header style={{ fontSize: '1em' }}>
                <div
                  title={proto.name || 'Untitled'}
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {proto.name || 'Untitled'}
                </div>
              </Card.Header>

              {proto.authorName && (
                <Card.Meta style={{ fontSize: '0.85em', marginTop: '0.3em' }}>
                  <Icon name="user" size="small" /> {proto.authorName}
                </Card.Meta>
              )}

              <Card.Description style={{ fontSize: '0.8em', color: '#888', marginTop: '0.5em' }}>
                <span>
                  <Icon name="file pdf outline" /> {bookCount} {bookCount === 1 ? 'book' : 'books'}
                </span>
                {proto.genre && (
                  <span style={{ marginLeft: '1em' }}>
                    <Icon name="tag" /> {proto.genre}
                  </span>
                )}
              </Card.Description>

              {/* Created date and status share a line — the date never filled it, and the status
                  is the one thing that should stay put across cards, so it is pinned right.
                  Rendered even without a created date so the badge does not disappear on a card
                  that happens to be missing one. */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '0.5em',
                gap: '0.5em',
              }}>
                <span style={{ fontSize: '0.75em', color: '#aaa' }}>
                  {proto.createdAt ? `Created ${formatDate(proto.createdAt)}` : ''}
                </span>
                <Label size="mini" color={status.color} style={{ flex: '0 0 auto' }}>
                  <Icon name={status.icon} />
                  {status.text}
                </Label>
              </div>
            </Card.Content>

            {loading.detail && isActive && (
              <Card.Content extra>
                <Loader active inline="centered" size="mini" />
              </Card.Content>
            )}
          </Card>
        );
      })}
      </div>
    </div>
  );
};

export default PrototypeList;
